import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CompanyBadgesTableRow, FilterProps } from "../../../../types";
import { ToolbarFilters } from "../filters/ToolbarFilters";
import { CreateBadgeButton } from "../../../../../../components/createBadge/createBadgeButton/CreateBadgeButton";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import { CompanyAdminsDialog } from "../adminsDialog/CompanyAdminsDialog";
import { BadgeEntitlementsDialog } from "../entitlementsDialog/BadgeEntitlementsDialog";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { CompanySearchResult } from "../../../../../../components/search/types";
import { useSelector } from "react-redux";
import { organisationsSelector } from "../../../../../../redux/organisations/selectors/organisation.selectors";
import EventHelper from "../../../../../../common/network/helpers/event/EventHelper";
import { Organisation } from "../../../../../../common/types";
import { useExhibitorDetailsData } from "../../hooks/useExhibitorDetailsData";

type Props = {
  tableData: CompanyBadgesTableRow[];
  selectedCompany?: CompanySearchResult;
} & Omit<FilterProps, "config" | "filterTableData">;

export const TableToolbar = ({ tableData, filterSetters, filterGetters, selectedCompany }: Props) => {
  const { t } = useTranslation("common");
  const organisations = useSelector(organisationsSelector);
  const organisationId = selectedCompany?.organisation.participatingOrganisationId;
  const [isAdminsDialogOpen, setAdminsDialogOpen] = useState(false);
  const [isEntitlementsDialogOpen, setEntitlementsDialogOpen] = useState(false);
  const isNetworkingEvent = EventHelper.eventEdition!.isParticipationEvent;

  const handleOpenAdmins = () => setAdminsDialogOpen(true);
  const handleCloseAdmins = () => setAdminsDialogOpen(false);

  const handleOpenEntitlements = () => setEntitlementsDialogOpen(true);
  const handleCloseEntitlements = () => setEntitlementsDialogOpen(false);

  const { participatingOrganisationId, exhibitorType } = useMemo(() => {
    if (!selectedCompany) return { participatingOrganisationId: undefined, exhibitorType: undefined };

    const organisation = organisations.find(
      org => org.id === organisationId || org.companyName === selectedCompany.organisation.displayName
    );

    return {
      participatingOrganisationId: organisation?.id ?? organisationId,
      exhibitorType: organisation?.exhibitorType
    };
  }, [organisations, selectedCompany, organisationId]);

  const { entitlements, isEntitlementsLoading, fetchEntitlements, isCreateBadgeEnabled } = useExhibitorDetailsData(
    participatingOrganisationId ?? null,
    exhibitorType
  );

  useEffect(() => {
    if (!participatingOrganisationId) return;
    fetchEntitlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participatingOrganisationId]);

  const selectedCompanyAccess = useMemo((): Organisation | undefined => {
    const matchedOrganisation = organisations.find(org => org.id === participatingOrganisationId);
    if (matchedOrganisation) {
      return matchedOrganisation;
    }

    if (selectedCompany && participatingOrganisationId) {
      const { displayName, city, countryCode, standIds } = selectedCompany.organisation;
      return {
        id: participatingOrganisationId,
        locale: "",
        companyName: displayName,
        addressLine1: "",
        city: city ?? "",
        postcode: "",
        countryCode: countryCode ?? "",
        exhibitorType: "",
        stands: (standIds ?? []).map(name => ({ name })),
        parentId: selectedCompany.organisation.parentId
      };
    }

    return undefined;
  }, [participatingOrganisationId, organisations, selectedCompany]);

  const companyName = selectedCompany?.organisation.displayName;
  const showExhibitorDetails = Boolean(participatingOrganisationId) && (isNetworkingEvent || exhibitorType !== "VisitingCompany");

  return (
    <>
      <Box
        sx={{
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          padding: 1
        }}
      >
        <Box sx={{ fontWeight: "bold" }}>{t("companyPage.table.badges")}</Box>
        :&nbsp;{tableData.length} {t("companyPage.table.total")}
        {showExhibitorDetails && (
          <Stack direction="row" spacing={0} sx={{ marginLeft: "auto", alignItems: "center" }}>
            <Button
              variant="text"
              startIcon={<PermIdentityIcon fontSize="small" />}
              onClick={handleOpenAdmins}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "14px" }}
            >
              {t("companyPage.adminsDialog.adminsButton")}
            </Button>

            <Button
              variant="text"
              startIcon={<BadgeOutlinedIcon fontSize="small" />}
              onClick={handleOpenEntitlements}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "14px", marginLeft: "15px", marginRight: "25px" }}
            >
              {t("companyPage.entitlementsDialog.entitlementsButton")}
            </Button>
            <CreateBadgeButton selectedCompanyAccess={selectedCompanyAccess} isCreateBadgeEnabled={Boolean(isCreateBadgeEnabled)} />
          </Stack>
        )}
      </Box>

      <ToolbarFilters filterSetters={filterSetters} filterGetters={filterGetters} />

      <CompanyAdminsDialog
        open={isAdminsDialogOpen}
        onClose={handleCloseAdmins}
        participatingOrganisationId={participatingOrganisationId!}
        companyName={companyName}
      />
      <BadgeEntitlementsDialog
        open={isEntitlementsDialogOpen}
        onClose={handleCloseEntitlements}
        entitlements={entitlements}
        isEntitlementsLoading={Boolean(isEntitlementsLoading)}
        companyName={companyName}
      />
    </>
  );
};
